import "./App.scss";
import React, { Suspense, useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Landing } from "./pages/Landing";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { NotFound } from "./components/NotFound";
import { GalleryProvider } from "./contexts/GalleryContext";

const Travel = React.lazy(() => import("./pages/travel/Travel").then(m => ({ default: m.Travel })));
const Resume = React.lazy(() => import("./pages/Resume").then(m => ({ default: m.Resume })));
const About = React.lazy(() => import("./pages/About").then(m => ({ default: m.About })));
const Gallery = React.lazy(() => import("./pages/travel/Gallery").then(m => ({ default: m.Gallery })));
const Stats = React.lazy(() => import("./pages/travel/Stats").then(m => ({ default: m.Stats })));
const CaseStudyHub = React.lazy(() => import("./case-studies/components/CaseStudyHub").then(m => ({ default: m.CaseStudyHub })));
const CaseStudyPage = React.lazy(() => import("./case-studies/components/CaseStudyPage").then(m => ({ default: m.CaseStudyPage })));

const App = () => {
  const [darkMode, setDarkMode] = useState(false);

  const setDark = () => {
    setDarkMode(true);
    localStorage.setItem("theme", "dark");
    document.documentElement.setAttribute("data-theme", "dark");
    let metaThemeColor = document.querySelector("meta[name=theme-color]");
    metaThemeColor.setAttribute("content", "#131313");
  };

  const setLight = () => {
    setDarkMode(false);
    localStorage.setItem("theme", "light");
    document.documentElement.setAttribute("data-theme", "light");
    let metaThemeColor = document.querySelector("meta[name=theme-color]");
    metaThemeColor.setAttribute("content", "#ffffff");
  };

  useEffect(() => {
    let storedTheme = localStorage.getItem("theme");
    setDarkMode(storedTheme === "dark");
    if (storedTheme === "dark") {
      setDark();
    } else {
      setLight();
    }
  }, []);

  const handleDarkModeToggle = () => {
    if (!darkMode) {
      setDark();
    } else {
      setLight();
    }
  };

  const [isMobileSafari, setIsMobileSafari] = useState(false);

  var ua = window.navigator.userAgent;
  var iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i);
  var webkit = !!ua.match(/WebKit/i);
  var iOSSafari = iOS && webkit && !ua.match(/CriOS/i);

  let domain = /:\/\/([^\/]+)/.exec(window.location.href)[1];

  useEffect(() => {
    setIsMobileSafari(iOSSafari);
  }, [iOSSafari, domain]);

  const ScrollToTop = (props) => {
    const location = useLocation();

    useEffect(() => {
      if (!window.sessionStorage.getItem("lastPosition")) {
        window.sessionStorage.setItem("lastPosition", "");
      }
      let lastPosition = window.sessionStorage.getItem("lastPosition");
      if (lastPosition === "") {
        window.sessionStorage.setItem("lastPosition", location.pathname);
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      }
      if (
        !lastPosition.includes("/work/") &&
        lastPosition !== location.pathname
      ) {
        window.sessionStorage.setItem("lastPosition", location.pathname);
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      }
      if (lastPosition.includes("/work/") && location.pathname !== "/work") {
        window.sessionStorage.setItem("lastPosition", location.pathname);
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      }
    }, [location]);

    return <>{props.children}</>;
  };

  document.addEventListener("touchstart", function () {}, true);

  window.addEventListener("scroll", () => {
    document.documentElement.style.setProperty(
      "--scroll-y",
      `${window.scrollY}px`
    );
  });

  const watchForHover = () => {
    // lastTouchTime is used for ignoring emulated mousemove events
    let lastTouchTime = 0;

    function enableHover() {
      if (new Date() - lastTouchTime < 500) return;
      document.body.classList.add("hasHover");
    }

    function disableHover() {
      document.body.classList.remove("hasHover");
    }

    function updateLastTouchTime() {
      lastTouchTime = new Date();
    }

    document.addEventListener("touchstart", updateLastTouchTime, true);
    document.addEventListener("touchstart", disableHover, true);
    document.addEventListener("mousemove", enableHover, true);

    enableHover();
  };

  watchForHover();

  return (
    <div>
      <Router>
        <div>
          <Header
            darkMode={darkMode}
            handleDarkModeToggle={handleDarkModeToggle}
          />
        </div>
        <ScrollToTop>
          <GalleryProvider>
            <Suspense fallback={null}>
            <Routes>
              <Route
                path="/"
                element={
                  <Landing
                    darkMode={darkMode}
                    isMobileSafari={isMobileSafari}
                  />
                }
              />
              <Route path="/about" element={<About />} />
              <Route path="/work" element={<CaseStudyHub darkMode={darkMode} />} />
              <Route
                path="/work/:slug"
                element={<CaseStudyPage darkMode={darkMode} />}
              />
              <Route path="/experience" element={<Resume />} />
              <Route
                path="/travel"
                element={
                  <Travel darkMode={darkMode} isMobileSafari={isMobileSafari} />
                }
              />
              <Route
                path="/travel/gallery"
                element={<Gallery darkMode={darkMode} />}
              />
              <Route
                path="/travel/gallery/:continent"
                element={<Gallery darkMode={darkMode} />}
              />
              <Route
                exact
                path="/travel/gallery/antarctica"
                element={<Navigate to="/travel/gallery" replace />}
              />
              <Route path="/travel/stats" element={<Stats />} />
              <Route path="/404" element={<NotFound />} />
              <Route path="*" element={<Navigate to="/404" replace />} />
            </Routes>
            </Suspense>
          </GalleryProvider>
        </ScrollToTop>
        <div>
          <Footer domain={domain} />
        </div>
      </Router>
    </div>
  );
};

export default App;
